import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Download, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, type ReactNode } from 'react';
import { getMexicoDateString } from '@/lib/mexicoDate';

interface AuditLog {
  id: number;
  action: string;
  timestamp: string;
  details: any;
  ipAddress: string | null;
  userEmail: string | null;
  userFullName: string | null;
}

interface AuditResponse {
  logs: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export default function Audit() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [error, setError] = useState<string | null>(null);

  // Converts a date-only <input type="date"> value ("YYYY-MM-DD") to the
  // full ISO 8601 datetime the backend's auditQuerySchema requires
  // (z.string().datetime() rejects a bare date string). "from" anchors to
  // the start of that Mexico-City day, "to" to the end, so the range is
  // inclusive of every record on the selected days regardless of the
  // browser's own timezone (hardcoding -06:00 is safe: Mexico Central has
  // had no DST since 2022).
  const toRangeStart = (dateOnly: string) => new Date(`${dateOnly}T00:00:00-06:00`).toISOString();
  const toRangeEnd = (dateOnly: string) => new Date(`${dateOnly}T23:59:59.999-06:00`).toISOString();

  // Load audit logs from API
  const fetchAuditLogs = async (page = 1, refresh = false, limit = pagination.limit) => {
    try {
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (filterAction !== 'all') {
        params.append('action', filterAction);
      }

      if (dateFrom) {
        params.append('from', toRangeStart(dateFrom));
      }

      if (dateTo) {
        params.append('to', toRangeEnd(dateTo));
      }

      const response = await fetch(`/api/audit?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar los logs de auditoría');
      }

      const data: AuditResponse = await response.json();
      setAuditLogs(data.logs);
      setPagination(data.pagination);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      console.error('Error fetching audit logs:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchAuditLogs(1);
  }, [filterAction, dateFrom, dateTo]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAuditLogs(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRefresh = () => {
    fetchAuditLogs(pagination.page, true);
  };

  const handlePageSizeChange = (newLimit: number) => {
    fetchAuditLogs(1, false, newLimit);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterAction !== 'all') params.append('action', filterAction);
      if (dateFrom) params.append('from', toRangeStart(dateFrom));
      if (dateTo) params.append('to', toRangeEnd(dateTo));

      const response = await fetch(`/api/audit/export?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al exportar los logs');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${getMexicoDateString()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      alert('Error al exportar los logs');
    }
  };

  const handlePageChange = (newPage: number) => {
    fetchAuditLogs(newPage);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('es-ES', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const FIELD_LABELS: Record<string, string> = {
    orderNumber: 'Orden',
    customerName: 'Cliente',
    productCode: 'Cód. Producto',
    materialName: 'Material',
    unit: 'Unidad',
    quantity: 'Cantidad',
    email: 'Email',
    tenant: 'Tenant',
    roleName: 'Rol',
    fromStatus: 'Estado anterior',
    toStatus: 'Estado nuevo',
    comment: 'Comentario',
    changedFields: 'Campos modificados',
    created: 'Creadas',
    updated: 'Actualizadas',
    skipped: 'Omitidas',
    totalOrders: 'Total órdenes',
    errorsCount: 'Errores',
    machineName: 'Máquina',
    permissions: 'Permisos',
    logoUrl: 'Logo',
    // Comercia
    idDoc: 'IdDoc',
    servicio: 'Servicio',
    resultado: 'Resultado',
    accion: 'Acción',
    totalMateriales: 'Total materiales',
    materialesFaltantes: 'Con faltante',
    error: 'Error',
  };

  const UUID_FIELD_NAMES = new Set(['orderId', 'materialId', 'machineId', 'userId', 'newUserId', 'updatedUserId', 'deletedUserId']);

  const formatDetails = (details: any): ReactNode => {
    if (!details) return <span className="text-gray-400">—</span>;
    if (typeof details === 'string') return <span>{details}</span>;
    if (typeof details !== 'object') return <span>{String(details)}</span>;

    const pairs: { label: string; value: string; highlight?: boolean }[] = [];

    for (const [key, value] of Object.entries(details)) {
      if (value === null || value === undefined) continue;

      if (UUID_FIELD_NAMES.has(key)) continue;
      if (typeof value === 'string' && UUID_REGEX.test(value)) continue;

      if (key === 'changes' || key === 'updates') {
        if (typeof value === 'object' && !Array.isArray(value)) {
          const fields = Object.keys(value as object).filter(k => k !== 'updatedAt' && k !== 'updatedBy');
          if (fields.length > 0) pairs.push({ label: 'Campos', value: fields.join(', ') });
          continue;
        }
      }

      if (key === 'logoUrl') {
        pairs.push({ label: 'Logo', value: 'actualizado' });
        continue;
      }

      if (Array.isArray(value)) {
        const arr = value as any[];
        if (arr.length > 0 && typeof arr[0] !== 'object') {
          pairs.push({ label: FIELD_LABELS[key] || key, value: arr.join(', ') });
        }
        continue;
      }

      if (typeof value === 'object') continue;

      const label = FIELD_LABELS[key] || key;
      const isHighlight = key === 'orderNumber' || key === 'materialName' || key === 'email' || key === 'roleName';
      pairs.push({ label, value: String(value), highlight: isHighlight });
    }

    if (pairs.length === 0) return <span className="text-gray-400">—</span>;

    return (
      <span className="flex flex-wrap gap-1">
        {pairs.map((p, i) => (
          <span key={i} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${p.highlight ? 'bg-blue-50 border-blue-200 text-blue-800 font-medium' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
            <span className="text-gray-400 font-normal">{p.label}:</span>
            <span>{p.value}</span>
          </span>
        ))}
      </span>
    );
  };

  const getActionColor = (action: string) => {
    if (action.includes('login')) return 'bg-green-100 text-green-800';
    if (action.includes('logout')) return 'bg-gray-100 text-gray-800';
    if (action.includes('comercia')) return 'bg-teal-100 text-teal-800';
    if (action.includes('create')) return 'bg-blue-100 text-blue-800';
    if (action.includes('update')) return 'bg-yellow-100 text-yellow-800';
    if (action.includes('delete')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('app.auditLogs')}</h2>
          <p className="text-muted-foreground">
            Monitor all system activities and user actions for compliance and security.
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>{error}</p>
              <Button onClick={() => fetchAuditLogs(1)} className="mt-4">
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">{t('app.auditLogs')}</h2>
        <p className="text-muted-foreground">
          Monitor all system activities and user actions for compliance and security.
        </p>
      </div>

      {/* Search and filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={`${t('common.search')} audit logs...`}
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Registros por página</label>
              <select
                className="px-3 py-2 border rounded-md text-sm"
                value={pagination.limit}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={150}>150</option>
              </select>
            </div>
            <div className="flex space-x-2">
              <select
                className="px-3 py-2 border rounded-md text-sm"
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
              >
                <option value="all">Todas las acciones</option>
                <option value="user_login">Login</option>
                <option value="user_created">Crear usuario</option>
                <option value="company_updated">Actualizar empresa</option>
                <option value="company_logo_updated">Logo actualizado</option>
                <option value="tenant_created">Tenant creado</option>
              </select>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Actualizando...' : 'Actualizar'}
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Exportar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit logs table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Recent system activities and user actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Fecha y Hora</th>
                  <th className="text-left py-3 px-4 font-medium">Acción</th>
                  <th className="text-left py-3 px-4 font-medium">Usuario</th>
                  <th className="text-left py-3 px-4 font-medium">IP</th>
                  <th className="text-left py-3 px-4 font-medium">Detalles</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      Cargando logs de auditoría...
                    </td>
                  </tr>
                ) : auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      No se encontraron logs de auditoría
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log.id} className="border-b">
                      <td className="py-3 px-4 text-sm">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {log.userEmail || log.userFullName || 'Sistema'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {log.ipAddress || '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {formatDetails(log.details)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <CardContent className="pt-0">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total} resultados
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1 || isLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm">
                  Página {pagination.page} de {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages || isLoading}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{pagination.total}</div>
            <p className="text-xs text-muted-foreground">Total logs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{auditLogs.filter(log => log.action.includes('login')).length}</div>
            <p className="text-xs text-muted-foreground">Logins (página actual)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{auditLogs.filter(log => log.action.includes('user_create')).length}</div>
            <p className="text-xs text-muted-foreground">Usuarios creados (página actual)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{auditLogs.filter(log => log.action.includes('update')).length}</div>
            <p className="text-xs text-muted-foreground">Actualizaciones (página actual)</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}